import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Audience as AudienceEnum } from '../content/contracts';
/**
 * `GET /api/onboarding/answers` and `POST /api/onboarding/answers`.
 *
 * Reused for the profile's Destinations picker.
 *
 * ── Invariant ─────────────────────────────────────────────────────────────
 * Onboarding answers is a **single-select** table: PK `(userId, questionId)`.
 * A destination is chosen implicitly by picking an option that names one
 * (`OnboardingOption.destinationId`). The current seed maps five of the six
 * destinations through the `season` question — the sixth (`screens` is
 * mapped; the outlier per seed is `self-care` mapped from `running-on-empty`,
 * meaning `anxiety` is currently un-mappable through the existing seeded
 * options).
 *
 * We did not add `POST /me/destinations` here because:
 *   1. Multi-select destination storage cannot be represented in
 *      `onboarding_answers` without relaxing the primary key — a migration,
 *      and this pass carries none.
 *   2. The mapping IS derivable: the recommendation engine already reads
 *      destination affinity off `OnboardingOption.destinationId`, so a
 *      duplicate `user_destinations` table would be the second source of
 *      truth this platform's docs already refuse.
 *
 * The webapp side compensates by treating "chosen" as the union of
 * destinationIds implied by the answered options. Toggling on picks the
 * option that names that destination; toggling off is not supported by the
 * endpoint (there is no delete for an answer, and `SubmitOnboardingAnswers`
 * requires `min(1)` answers) — the UI reflects this by keeping a chosen chip
 * until the member picks a different one.
 */
export const OnboardingAnswer = z.object({
    questionId: z.string().uuid(),
    questionKey: z.string(),
    optionId: z.string().uuid(),
    optionKey: z.string(),
    answeredAt: z.string().datetime(),
});
const AnswersEnvelope = z.object({ items: z.array(OnboardingAnswer) });
const OnboardingOption = z.object({
    id: z.string().uuid(),
    key: z.string(),
    position: z.number().nonnegative(),
    destinationId: z.string().uuid().nullable(),
    ageBandId: z.string().uuid().nullable(),
    contentItemId: z.string().uuid().nullable(),
    label: z.string(),
});
export const OnboardingQuestion = z.object({
    id: z.string().uuid(),
    key: z.string(),
    position: z.number().nonnegative(),
    required: z.boolean(),
    prompt: z.string(),
    /**
     * Who this question is asked of. **Empty means the PARENT set** — the
     * opposite of the identically-named field on `Destination`, where empty
     * means unrestricted.
     *
     * The asymmetry is the backend's and it is the right way round. The three
     * questions that predate the field are a parent's — "How old is your child?"
     * — so reading their silence as *ask this of anybody* is exactly the defect
     * that had a psychoeducator being asked it. A destination label, by
     * contrast, is harmless wherever it appears.
     */
    audiences: z.array(AudienceEnum),
    options: z.array(OnboardingOption),
});
const QuestionnaireEnvelope = z.object({
    items: z.array(OnboardingQuestion),
    locale: z.string(),
});
export const SubmitOnboardingAnswers = z.object({
    answers: z
        .array(z.object({ questionId: z.string().uuid(), optionId: z.string().uuid() }))
        .min(1),
});
export function useOnboardingAnswers(http) {
    return useQuery({
        queryKey: ['onboarding', 'answers'],
        queryFn: () => http.get('/onboarding/answers', { schema: AnswersEnvelope }),
        staleTime: 60_000,
    });
}
/**
 * The question set for ONE audience.
 *
 * ── `audience` is required here, and it is not a permission ───────────────
 * `?audience=` on a content read narrows a pool the token already bounds. This
 * one does not: signup happens before an account exists, so the caller names
 * the set it wants and the server withholds nothing. Asking for the wrong one
 * reveals nothing and returns the wrong questions, which is why it is required
 * rather than defaulted — omitting it answers the PARENT set, and that silent
 * default is precisely what served "How old is your child?" to every
 * practitioner on the platform.
 *
 * `undefined` holds the query rather than guessing, for the moment before the
 * session has resolved.
 */
export function useOnboardingQuestionnaire(http, locale, audience) {
    return useQuery({
        queryKey: ['onboarding', 'questionnaire', locale, audience],
        enabled: Boolean(audience),
        queryFn: () => http.get('/onboarding/questionnaire', {
            query: { audience: audience },
            schema: QuestionnaireEnvelope,
            locale,
        }),
        staleTime: 30 * 60_000,
    });
}
/**
 * Write the whole answer set. The API returns every answer on record — not
 * only the ones sent — so the cache is replaced rather than invalidated.
 */
export function useSetOnboardingAnswers(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => http.post('/onboarding/answers', SubmitOnboardingAnswers.parse(input), {
            schema: AnswersEnvelope,
        }),
        onSuccess: (data) => queryClient.setQueryData(['onboarding', 'answers'], data),
    });
}
//# sourceMappingURL=onboarding.js.map